import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import { Stack, Box } from "@mui/material";

import { Chapter } from "../types/Chapter";
import { VerseModel } from "../types/VerseModel";
import { Edition } from "../enums/Edition";
import { toAllah } from "../utils/ToAllah";

const closeButton = {
  position: "absolute" as const,
  right: 8,
  top: 8,
} as const;

const content = {
  marginTop: "20px",
  marginBottom: "30px",
  marginLeft: "30px",
  marginRight: "30px",
  padding: "0px",
  width: "100px",
} as const;

const formControl = {
  margin: "0px",
  marginBottom: "30px",
  width: "100px",
} as const;

const shareButton = {
  width: "100px",
  marginTop: "5px",
} as const;

interface ShareVersesDialogProps {
  currentVerseNumber: number;
  chapter: Chapter;
  isOpen: boolean;
  edition: Edition;
  handleCloseDialog: () => void;
}

function ShareVersesDialogImpl(props: ShareVersesDialogProps): JSX.Element {
  const { chapter, edition } = props;

  // Quick O(1) verse lookup instead of filtering arrays repeatedly
  const verseMap = React.useMemo(() => {
    const m = new Map<number, VerseModel>();
    for (const v of chapter.verses) m.set(v.verseNumber, v);
    return m;
  }, [chapter.verses]);

  // Prebuild the menu items once per chapter
  const verseOptions = React.useMemo(
    () =>
      chapter.verses.map(v => (
        <MenuItem value={v.verseNumber} key={v.verseNumber} id={`v-${v.verseNumber}`}>
          {v.verseNumber}
        </MenuItem>
      )),
    [chapter.verses],
  );

  const [fromVerseNumber, setFromVerseNumber] = React.useState(props.currentVerseNumber);
  const [toVerseNumber, setToVerseNumber] = React.useState(props.currentVerseNumber);

  const handleFromVerseChange = React.useCallback((event: SelectChangeEvent<number>) => {
    setFromVerseNumber(Number(event.target.value));
  }, []);

  const handleToVerseChange = React.useCallback((event: SelectChangeEvent<number>) => {
    setToVerseNumber(Number(event.target.value));
  }, []);

  const onClickShareButton = React.useCallback(() => {
    const min = Math.min(fromVerseNumber, toVerseNumber);
    const max = Math.max(fromVerseNumber, toVerseNumber);

    let text = "";
    for (let i = min; i <= max; i++) {
      const vm = verseMap.get(i);
      if (!vm) continue;
      const verseLabel = i === 0 ? "" : `${i}. `;
      const line = edition === Edition.God ? vm.verseString : toAllah(vm.verseString);
      text += `${verseLabel}${line}\r\n\r\n`;
    }

    // Share the anchor for the first verse in range
    const url = `#${min}`;

    // Web Share when available; otherwise copy to clipboard as a graceful fallback
    const canShare = typeof navigator !== "undefined" && (navigator as any).share;
    if (canShare) {
      (navigator as any)
        .share({
          title: "Quran",
          text,
          url,
        })
        .catch(() => {
          // ignore if user cancels
        });
    } else if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(`${text}\n${url}`).catch(() => {});
      // Optional: surface a snackbar from parent; here we just close.
    }

    props.handleCloseDialog();
  }, [edition, fromVerseNumber, toVerseNumber, verseMap, props]);

  React.useEffect(() => {
    setFromVerseNumber(props.currentVerseNumber);
    setToVerseNumber(props.currentVerseNumber);
  }, [props.currentVerseNumber]);

  return (
    <Dialog
      onClose={props.handleCloseDialog}
      aria-labelledby="share-verses-title"
      open={props.isOpen}
      keepMounted  // faster first open
    >
      <DialogTitle id="share-verses-title">
        <IconButton aria-label="Close" sx={closeButton} onClick={props.handleCloseDialog} size="large">
          <CloseIcon />
        </IconButton>
        Share Verses
      </DialogTitle>

      <DialogContent dividers>
        <Stack sx={content} direction="column">
          <FormControl sx={formControl}>
            <InputLabel id="from-verse-label">From Verse</InputLabel>
            <Select<number>
              labelId="from-verse-label"
              id="from-selector"
              variant="outlined"
              value={fromVerseNumber}
              onChange={handleFromVerseChange}
              label="From Verse"
            >
              {verseOptions}
            </Select>
          </FormControl>

          <FormControl sx={formControl}>
            <InputLabel id="to-verse-label">To Verse</InputLabel>
            <Select<number>
              labelId="to-verse-label"
              id="to-selector"
              variant="outlined"
              value={toVerseNumber}
              onChange={handleToVerseChange}
              label="To Verse"
            >
              {verseOptions}
            </Select>
          </FormControl>

          <Box>
            <Button
              id="share-verses-button"
              variant="contained"
              color="primary"
              sx={shareButton}
              type="button"
              onClick={onClickShareButton}
            >
              &nbsp;Share&nbsp;
            </Button>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default React.memo(ShareVersesDialogImpl, (a, b) => {
  return (
    a.isOpen === b.isOpen &&
    a.currentVerseNumber === b.currentVerseNumber &&
    a.edition === b.edition &&
    a.chapter === b.chapter && // chapter identity usually stable across dialog opens
    a.handleCloseDialog === b.handleCloseDialog
  );
});
